import { z } from "zod";

import type { RouteContract } from "./types";

/**
 * Contracts for the "plugins" route family — the connect/sync/credential
 * surface the plugins UI fetches, plus the custom-API-tool create/test routes.
 *
 * Pure zod (no db / server-only / node imports) so the browser client and the
 * route handlers can both import it. Tenancy/ownership checks (orgId/userId
 * scoping, 404s, the Salesforce SOQL-injection boundary in sync-config) live in
 * the handlers, NOT here — these schemas only describe the wire shape.
 *
 * GET-with-query routes here actually take no query params, so they use
 * `request: null` and a static path.
 */

// ---------------------------------------------------------------------------
// GET /api/plugins/slack/channels — accessible Slack channels for the org.
// ---------------------------------------------------------------------------
export const slackChannelOption = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.number().optional(),
  isMember: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
});

export const slackChannelsResponse = z.object({
  channels: z.array(slackChannelOption),
});

export const slackChannelsContract: RouteContract<
  null,
  typeof slackChannelsResponse
> = {
  method: "GET",
  path: "/api/plugins/slack/channels",
  request: null,
  response: slackChannelsResponse,
};

// ---------------------------------------------------------------------------
// GET /api/plugins/slack/users — Slack workspace users for the org.
// ---------------------------------------------------------------------------
export const slackUserOption = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable().optional(),
  realName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  isBot: z.boolean().optional(),
});

export const slackUsersResponse = z.object({
  users: z.array(slackUserOption),
});

export const slackUsersContract: RouteContract<
  null,
  typeof slackUsersResponse
> = {
  method: "GET",
  path: "/api/plugins/slack/users",
  request: null,
  response: slackUsersResponse,
};

// ---------------------------------------------------------------------------
// GET /api/plugins/salesforce/field-options — sync rule-builder field catalog.
// The error path returns { ok: false, message }; the contract models the
// success body the client consumes (it reads body.catalog).
// ---------------------------------------------------------------------------
export const salesforceFieldCatalog = z.object({
  owners: z.object({
    field: z.literal("OwnerId"),
    label: z.string(),
    options: z.array(z.object({ id: z.string(), name: z.string() })),
    hasMore: z.boolean(),
  }),
  picklists: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      options: z.array(z.object({ value: z.string(), label: z.string() })),
    }),
  ),
});

export const salesforceFieldOptionsResponse = z.object({
  ok: z.literal(true),
  catalog: salesforceFieldCatalog,
});

export const salesforceFieldOptionsContract: RouteContract<
  null,
  typeof salesforceFieldOptionsResponse
> = {
  method: "GET",
  path: "/api/plugins/salesforce/field-options",
  request: null,
  response: salesforceFieldOptionsResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/:slug/api-key — connect an API-key plugin.
// NOTE: the handler is LENIENT (coerces a non-string / missing apiKey to "" and
// 400s with a dynamic credentialLabel message), so it is NOT adopted
// server-side. This schema types the CLIENT call only — the client always sends
// a string.
// ---------------------------------------------------------------------------
export const pluginApiKeyRequest = z.object({
  apiKey: z.string(),
});

export const pluginConnectResponse = z.object({
  ok: z.literal(true),
  externalAccountId: z.string(),
});

export const pluginApiKeyContract: RouteContract<
  typeof pluginApiKeyRequest,
  typeof pluginConnectResponse
> = {
  method: "POST",
  path: (params) => `/api/plugins/${params.slug}/api-key`,
  request: pluginApiKeyRequest,
  response: pluginConnectResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/:slug/credentials — connect a static-credentials plugin.
// Handler is LENIENT (normalizes the credentials map, trims, 400s per missing
// required field with a dynamic label) — NOT adopted server-side.
// ---------------------------------------------------------------------------
export const pluginCredentialsRequest = z.object({
  credentials: z.record(z.string(), z.string()),
});

export const pluginCredentialsContract: RouteContract<
  typeof pluginCredentialsRequest,
  typeof pluginConnectResponse
> = {
  method: "POST",
  path: (params) => `/api/plugins/${params.slug}/credentials`,
  request: pluginCredentialsRequest,
  response: pluginConnectResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/:slug/disconnect — bodyless; archives the connection.
// Two success shapes: { ok, message } ("No active connection.") and
// { ok, teardownError }. Both fields optional.
// ---------------------------------------------------------------------------
export const pluginDisconnectResponse = z.object({
  ok: z.literal(true),
  teardownError: z.string().nullable().optional(),
  message: z.string().optional(),
});

export const pluginDisconnectContract: RouteContract<
  null,
  typeof pluginDisconnectResponse
> = {
  method: "POST",
  path: (params) => `/api/plugins/${params.slug}/disconnect`,
  request: null,
  response: pluginDisconnectResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/:slug/sync — bodyless; runs a manual sync/ingestion pass.
// Success body is { ok, counters? }; the non-2xx { ok: false, message } 409/502
// is surfaced as an error by the client.
// ---------------------------------------------------------------------------
export const pluginSyncResponse = z.object({
  ok: z.literal(true),
  counters: z.record(z.string(), z.number()).optional(),
  status: z.enum(["queued", "already_running"]).optional(),
});

export const pluginSyncContract: RouteContract<
  null,
  typeof pluginSyncResponse
> = {
  method: "POST",
  path: (params) => `/api/plugins/${params.slug}/sync`,
  request: null,
  response: pluginSyncResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/notion/import-plan — scan connected Notion content and
// return a reviewable import plan. Notion-specific because the response shape
// reflects source-map classifications and Notion object labels.
// ---------------------------------------------------------------------------
export const notionImportPlanItem = z.object({
  externalId: z.string(),
  kind: z.string(),
  title: z.string(),
  targetName: z.string().nullable(),
  domain: z.string().nullable(),
  url: z.string().nullable(),
  parentId: z.string().nullable(),
  confidence: z.number().nullable(),
  reason: z.string().nullable(),
});

export const notionImportPlanSection = z.object({
  id: z.enum(["companyKnowledge", "clientPages", "ops", "ignored"]),
  title: z.string(),
  summary: z.string(),
  count: z.number(),
  items: z.array(notionImportPlanItem),
});

export const notionImportPlanResponse = z.object({
  ok: z.literal(true),
  plan: z.object({
    overview: z.string().nullable(),
    totals: z.object({
      scanned: z.number(),
      companyKnowledge: z.number(),
      clientPages: z.number(),
      ops: z.number(),
      ignored: z.number(),
    }),
    traceSteps: z.array(
      z.object({
        title: z.string(),
        detail: z.string(),
        status: z.literal("completed"),
      }),
    ),
    sections: z.array(notionImportPlanSection),
  }),
});

export const notionImportPlanContract: RouteContract<
  null,
  typeof notionImportPlanResponse
> = {
  method: "POST",
  path: "/api/plugins/notion/import-plan",
  request: null,
  response: notionImportPlanResponse,
};

// ---------------------------------------------------------------------------
// POST /api/plugins/notion/create-sync — approve the setup-chat plan and start
// the first Notion knowledge import in the background. configuredAt marks when
// the user accepted the sync shape.
// ---------------------------------------------------------------------------
export const notionCreateSyncResponse = z.object({
  ok: z.literal(true),
  configuredAt: z.string(),
  status: z.literal("started"),
  counters: z.record(z.string(), z.number()).optional(),
});

export const notionCreateSyncContract: RouteContract<
  null,
  typeof notionCreateSyncResponse
> = {
  method: "POST",
  path: "/api/plugins/notion/create-sync",
  request: null,
  response: notionCreateSyncResponse,
};

// ---------------------------------------------------------------------------
// PATCH /api/plugins/:slug/sync-config — persist a sync preset or custom scope.
// Handler is LENIENT on optionId and runs the SECURITY-CRITICAL
// userAuthoredCustomerScopeSchema parse on `scope` (the SOQL-injection
// boundary). NOT adopted server-side — this schema types the client call; the
// handler keeps owning scope validation.
// ---------------------------------------------------------------------------
export const pluginSyncConfigRequest = z.object({
  optionId: z.string().optional(),
  scope: z.unknown().optional(),
});

export const pluginSyncConfigResponse = z.object({
  ok: z.literal(true),
  selectedOptionId: z.string(),
  config: z.record(z.string(), z.unknown()),
});

export const pluginSyncConfigContract: RouteContract<
  typeof pluginSyncConfigRequest,
  typeof pluginSyncConfigResponse
> = {
  method: "PATCH",
  path: (params) => `/api/plugins/${params.slug}/sync-config`,
  request: pluginSyncConfigRequest,
  response: pluginSyncConfigResponse,
};

// ---------------------------------------------------------------------------
// Custom API tools — POST /api/custom-api-tools (create) and
// POST /api/custom-api-tools/test (dry-run). Both routes parse their body with
// the SAME schema, which lives here as the single source of truth;
// lib/custom-api-tools/validation.ts re-exports it (validation.ts can't be
// imported by the browser — it pulls in node:net — so the canonical schema must
// live in this pure module). Both handlers ALREADY strictly 400'd on a schema
// miss (with { message, issues }); they keep doing so via this exact schema, so
// adoption is behavior-preserving.
// ---------------------------------------------------------------------------
export const customApiInputFieldSchema = z.object({
  key: z
    .string()
    .min(1)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      "Use a variable name like email or accountId.",
    ),
  label: z.string().min(1),
  type: z.enum(["string", "string[]", "object", "any"]).default("string"),
  description: z.string().optional(),
  example: z.string().optional(),
  format: z.string().optional(),
  required: z.boolean().default(true),
  multiline: z.boolean().default(false),
});

export const customApiToolRequest = z.object({
  name: z.string().min(1).max(80),
  description: z.string().max(500).optional().default(""),
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]).default("POST"),
  url: z.string().min(1),
  authType: z.enum(["none", "bearer"]).default("none"),
  bearerToken: z.string().optional(),
  headers: z.record(z.string(), z.string()).optional().default({}),
  inputFields: z.array(customApiInputFieldSchema).max(20).default([]),
  bodyTemplate: z.string().max(20000).optional().default(""),
});

const customApiToolDto = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  method: z.string(),
  url: z.string(),
  authType: z.string(),
  inputCount: z.number(),
  updatedAt: z.string(),
});

export const customApiToolCreateResponse = z.object({
  ok: z.literal(true),
  tool: customApiToolDto,
});

export const customApiToolCreateContract: RouteContract<
  typeof customApiToolRequest,
  typeof customApiToolCreateResponse
> = {
  method: "POST",
  path: "/api/custom-api-tools",
  request: customApiToolRequest,
  response: customApiToolCreateResponse,
};

export const customApiToolTestResponse = z.object({
  ok: z.literal(true),
  status: z.number(),
  body: z.unknown(),
});

export const customApiToolTestContract: RouteContract<
  typeof customApiToolRequest,
  typeof customApiToolTestResponse
> = {
  method: "POST",
  path: "/api/custom-api-tools/test",
  request: customApiToolRequest,
  response: customApiToolTestResponse,
};

// --- inferred types ---------------------------------------------------------
export type SlackChannelsResponse = z.infer<typeof slackChannelsResponse>;
export type SlackUsersResponse = z.infer<typeof slackUsersResponse>;
export type SalesforceFieldOptionsResponse = z.infer<
  typeof salesforceFieldOptionsResponse
>;
export type PluginApiKeyRequest = z.input<typeof pluginApiKeyRequest>;
export type PluginCredentialsRequest = z.input<typeof pluginCredentialsRequest>;
export type PluginConnectResponse = z.infer<typeof pluginConnectResponse>;
export type PluginDisconnectResponse = z.infer<typeof pluginDisconnectResponse>;
export type PluginSyncResponse = z.infer<typeof pluginSyncResponse>;
export type NotionCreateSyncResponse = z.infer<typeof notionCreateSyncResponse>;
export type PluginSyncConfigRequest = z.input<typeof pluginSyncConfigRequest>;
export type PluginSyncConfigResponse = z.infer<typeof pluginSyncConfigResponse>;
export type CustomApiToolRequest = z.input<typeof customApiToolRequest>;
export type CustomApiToolRequestParsed = z.infer<typeof customApiToolRequest>;
export type CustomApiToolCreateResponse = z.infer<
  typeof customApiToolCreateResponse
>;
export type CustomApiToolTestResponse = z.infer<
  typeof customApiToolTestResponse
>;
